import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { Button } from "@faire/slate/components/button";
import { TextInput } from "@faire/slate/components/text-input";
import { CheckOutlineSolid } from "@faire/slate-icons/CheckOutlineSolid";
import { Redo } from "@faire/slate-icons/Redo";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

import type { LoaderStyle } from "./CalculatingScreen";
import { CalculatingScreen } from "./CalculatingScreen";
import { NetTermsRevealScreen } from "./NetTermsRevealScreen";
import { EASE_OUT_EXPO, NET_TERMS, useReplayKey } from "./shared";
import { SignupStepHeader } from "./SignupStepHeader";

export const prototypeMeta: PrototypeMetadata = {
  name: "Calculating Net Terms",
  owner: "Michael Zhang",
  email: "michael.zhang@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-04",
  lastUpdated: "2026-08-04",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "https://cdn.prerelease.faire.dev/fastly/699e71030d373c6c545d270c7b9494bc1de70ee84f4157afbddc9d4ff9a46fda.png",
  description:
    "Signup flow that pauses on a loading screen calculating the retailer's net terms, then reveals an approved net-60 limit as a celebratory gift before continuing.",
};

type FlowPhase = "form" | "calculating" | "reveal" | "home";

const LOADER_CHECKLIST = 1;
const LOADER_RING = 2;

const LOADER_STYLE_BY_STATE: Record<number, LoaderStyle> = {
  [LOADER_CHECKLIST]: "checklist",
  [LOADER_RING]: "ring",
};

const SPEED_QUICK = 1600;
const SPEED_STANDARD = 3000;
const SPEED_THOROUGH = 4800;

const CONFETTI_COUNT_OPTIONS = [
  { stateNumber: 0, label: "0" },
  { stateNumber: 60, label: "60" },
  { stateNumber: 100, label: "100" },
  { stateNumber: 160, label: "160" },
];

const PHASE_CAPTIONS: Record<FlowPhase, string> = {
  form: "A stand-in earlier signup step — the net-terms moment always comes after the retailer has entered the flow, never as a cold pop-up.",
  calculating:
    "The loading step deliberately takes a beat to “calculate” terms, building anticipation so the number that follows feels earned.",
  reveal:
    "The payoff: an emphatically celebratory reveal that frames 60-day terms as a gift Faire is giving the retailer, with the benefits spelled out.",
  home: "Net terms stays visible after signup via a persistent banner, so the perk keeps paying off while they shop.",
};

export default function CalculatingNetTermsPage() {
  const [phase, setPhase] = useState<FlowPhase>("form");
  const [storeName, setStoreName] = useState("");
  const [loaderStyle, setLoaderStyle] = useState(LOADER_CHECKLIST);
  const [calculatingSpeed, setCalculatingSpeed] = useState(SPEED_STANDARD);
  const [confettiCount, setConfettiCount] = useState(100);
  const [replayKey, replay] = useReplayKey();

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  const restartFromTop = (): void => {
    replay();
    setStoreName("");
    setPhase("form");
  };

  return (
    <>
      <LiveToolbar
        variantGroups={[
          {
            title: "Loader style",
            queryKey: "loaderStyle",
            variants: [
              { variantNumber: LOADER_CHECKLIST, label: "Checklist steps" },
              { variantNumber: LOADER_RING, label: "Progress ring" },
            ],
            active: loaderStyle,
            onChange: setLoaderStyle,
          },
        ]}
        stateGroups={[
          {
            title: "Calculating speed",
            queryKey: "calculatingSpeed",
            description:
              "How long the loading step spends “calculating” before revealing the terms.",
            states: [
              { stateNumber: SPEED_QUICK, label: "Quick" },
              { stateNumber: SPEED_STANDARD, label: "Standard" },
              { stateNumber: SPEED_THOROUGH, label: "Thorough" },
            ],
            active: calculatingSpeed,
            onChange: setCalculatingSpeed,
            sliderMode: true,
            showNotches: true,
          },
          {
            title: "Confetti",
            queryKey: "confetti",
            description: "How many confetti pieces fall on the reveal.",
            states: CONFETTI_COUNT_OPTIONS,
            active: confettiCount,
            onChange: setConfettiCount,
            sliderMode: true,
            showNotches: true,
          },
        ]}
      />

      <div className="bg-fs-surface-primary min-h-screen overflow-x-hidden">
        <AnimatePresence mode="wait" initial={false}>
          {phase === "form" ? (
            <StoreDetailsStep
              key={`form-${replayKey}`}
              storeName={storeName}
              onStoreNameChange={setStoreName}
              onContinue={() => setPhase("calculating")}
            />
          ) : phase === "calculating" ? (
            <CalculatingScreen
              key={`calculating-${replayKey}`}
              durationMs={calculatingSpeed}
              loaderStyle={LOADER_STYLE_BY_STATE[loaderStyle]}
              onComplete={() => setPhase("reveal")}
            />
          ) : phase === "reveal" ? (
            <NetTermsRevealScreen
              key={`reveal-${replayKey}`}
              confettiCount={confettiCount}
              onContinue={() => setPhase("home")}
            />
          ) : (
            <HomeConfirmation key="home" onReplay={restartFromTop} />
          )}
        </AnimatePresence>
      </div>

      <div className="pointer-events-none fixed inset-x-0 bottom-(--slate-dimensions-4) z-3 flex justify-center px-(--slate-dimensions-4)">
        <p className="bg-fs-surface-primary-inverse text-fs-text-primary-inverse type-fs-label-medium shadow-elevation-medium pointer-events-auto max-w-[560px] rounded-full px-(--slate-dimensions-4) py-(--slate-dimensions-2) text-center">
          {PHASE_CAPTIONS[phase]}
        </p>
      </div>
    </>
  );
}

/** A stand-in earlier signup step, establishing that the net-terms flow always follows the retailer into signup. */
const StoreDetailsStep: React.FC<{
  storeName: string;
  onStoreNameChange: (storeName: string) => void;
  onContinue: () => void;
}> = ({ storeName, onStoreNameChange, onContinue }) => {
  return (
    <motion.div
      exit={{ opacity: 0, transition: { duration: 0.3, ease: EASE_OUT_EXPO } }}
      className="bg-fs-surface-primary flex min-h-screen w-full flex-col"
    >
      <SignupStepHeader progressPercent={72} onBack={() => undefined} />
      <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col gap-(--slate-spacing-group-xlarge) px-(--slate-spacing-layout-edge-mobile) py-(--slate-spacing-section-large)">
        <div className="flex flex-col gap-(--slate-dimensions-2)">
          <h1 className="type-fs-display-s text-fs-text-primary">
            Tell us about your store
          </h1>
          <p className="type-fs-paragraph text-fs-text-subdued">
            A couple more details and we'll set up your account — including your
            payment terms.
          </p>
        </div>
        <TextInput
          label="Store name"
          placeholder="e.g. Corner Market"
          value={storeName}
          onChange={(event) => onStoreNameChange(event.target.value)}
        />
        <TextInput
          label="Store website"
          placeholder="e.g. cornermarket.com"
          optional
        />
        <div className="mt-(--slate-dimensions-2)">
          <Button variant="primary" onClick={onContinue}>
            Continue
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

/** Lightweight post-signup landing that keeps the net-terms perk visible while shopping. */
const HomeConfirmation: React.FC<{ onReplay: () => void }> = ({ onReplay }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
      className="bg-fs-surface-primary flex min-h-screen w-full flex-col"
    >
      <div className="bg-fs-message-surface-success flex w-full items-center justify-center gap-(--slate-dimensions-2) px-(--slate-dimensions-4) py-(--slate-dimensions-3)">
        <CheckOutlineSolid className="text-fs-icon-success size-5" />
        <span className="type-fs-label-medium text-fs-text-primary text-center">
          {NET_TERMS.homeBannerText}
        </span>
      </div>
      <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col items-center justify-center gap-(--slate-spacing-group-large) px-(--slate-spacing-layout-edge-mobile) text-center">
        <h1 className="type-fs-display-m text-fs-text-primary text-balance">
          You're all set — start shopping
        </h1>
        <p className="type-fs-paragraph text-fs-text-subdued text-balance">
          This is where the marketplace home would load. Your net terms follow
          you in, so you can fill a cart today and pay in {NET_TERMS.termDays}{" "}
          days.
        </p>
        <Button
          variant="secondary"
          size="small"
          onClick={onReplay}
          iconProps={{ Component: Redo, position: "start" }}
        >
          Replay from the top
        </Button>
      </div>
    </motion.div>
  );
};
